import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { scansApi, patientsApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Baby,
  Save,
  Ruler,
  Move3d,
  RotateCw,
  RotateCcw,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import { FetalHead3DViewer } from '../../components/FetalHead3DViewer';
import { PercentileChart, PercentileSummary, PercentileRangeIndicator } from '../../components/PercentileChart';

// Haftalık fetal biyometri verileri (18-40 hafta, p5-p50-p95)
const FETAL_DATA = {
  18: { bpd: { p5: 34.6, p50: 39.5, p95: 44.4 }, hc: { p5: 134.7, p50: 151.1, p95: 167.5 } },
  19: { bpd: { p5: 37.8, p50: 43.0, p95: 48.2 }, hc: { p5: 147.1, p50: 164.1, p95: 181.1 } },
  20: { bpd: { p5: 41.0, p50: 46.4, p95: 51.8 }, hc: { p5: 159.2, p50: 176.8, p95: 194.4 } },
  21: { bpd: { p5: 44.1, p50: 49.7, p95: 55.3 }, hc: { p5: 171.0, p50: 189.2, p95: 207.4 } },
  22: { bpd: { p5: 47.2, p50: 53.0, p95: 58.8 }, hc: { p5: 182.5, p50: 201.3, p95: 220.1 } },
  23: { bpd: { p5: 50.1, p50: 56.2, p95: 62.3 }, hc: { p5: 193.6, p50: 213.0, p95: 232.4 } },
  24: { bpd: { p5: 53.0, p50: 59.3, p95: 65.6 }, hc: { p5: 204.4, p50: 224.4, p95: 244.4 } },
  25: { bpd: { p5: 55.8, p50: 62.3, p95: 68.8 }, hc: { p5: 214.8, p50: 235.4, p95: 256.0 } },
  26: { bpd: { p5: 58.6, p50: 65.3, p95: 72.0 }, hc: { p5: 224.8, p50: 246.0, p95: 267.2 } },
  27: { bpd: { p5: 61.1, p50: 68.1, p95: 75.1 }, hc: { p5: 234.4, p50: 256.2, p95: 278.0 } },
  28: { bpd: { p5: 63.6, p50: 70.8, p95: 78.0 }, hc: { p5: 243.7, p50: 266.1, p95: 288.5 } },
  29: { bpd: { p5: 66.1, p50: 73.5, p95: 80.9 }, hc: { p5: 252.5, p50: 275.5, p95: 298.5 } },
  30: { bpd: { p5: 68.4, p50: 76.0, p95: 83.6 }, hc: { p5: 260.8, p50: 284.4, p95: 308.0 } },
  31: { bpd: { p5: 70.5, p50: 78.4, p95: 86.3 }, hc: { p5: 268.7, p50: 292.9, p95: 317.1 } },
  32: { bpd: { p5: 72.6, p50: 80.7, p95: 88.8 }, hc: { p5: 276.1, p50: 300.9, p95: 325.7 } },
  33: { bpd: { p5: 74.6, p50: 82.9, p95: 91.2 }, hc: { p5: 283.0, p50: 308.4, p95: 333.8 } },
  34: { bpd: { p5: 76.5, p50: 85.0, p95: 93.5 }, hc: { p5: 289.5, p50: 315.5, p95: 341.5 } },
  35: { bpd: { p5: 78.3, p50: 87.0, p95: 95.7 }, hc: { p5: 295.4, p50: 322.0, p95: 348.6 } },
  36: { bpd: { p5: 79.8, p50: 88.8, p95: 97.8 }, hc: { p5: 300.7, p50: 327.9, p95: 355.1 } },
  37: { bpd: { p5: 81.3, p50: 90.5, p95: 99.7 }, hc: { p5: 305.5, p50: 333.3, p95: 361.1 } },
  38: { bpd: { p5: 82.7, p50: 92.1, p95: 101.5 }, hc: { p5: 309.8, p50: 338.2, p95: 366.6 } },
  39: { bpd: { p5: 83.9, p50: 93.5, p95: 103.1 }, hc: { p5: 313.5, p50: 342.5, p95: 371.5 } },
  40: { bpd: { p5: 84.9, p50: 94.8, p95: 104.7 }, hc: { p5: 316.5, p50: 346.1, p95: 375.7 } }
};

// Hafta bazlı varsayılan değerleri al
const getDefaultsForWeek = (week) => {
  const data = FETAL_DATA[week] || FETAL_DATA[23];
  return {
    bpd: data.bpd.p50,
    hc: data.hc.p50,
    fromen: 25 + (week - 18) * 0.5,      // Alın yüksekliği tahmini
    goztepe: 15 + (week - 18) * 0.3,     // Göz arası mesafe tahmini
    goz: 8 + (week - 18) * 0.2,          // Göz boyutu tahmini
    burun: 6 + (week - 18) * 0.3,        // Burun uzunluğu tahmini
    bioc_cap: 35 + (week - 18) * 1,      // Yüz genişliği tahmini
    agiz_capi: 10 + (week - 18) * 0.3,   // Ağız genişliği tahmini
    ust_dudak: 5 + (week - 18) * 0.2,    // Üst dudak tahmini
    cene: 18 + (week - 18) * 0.4,        // Çene uzunluğu tahmini
    onarka_bas: 50 + (week - 18) * 2     // Önarka baş (OFD) tahmini
  };
};

// Hafta bazlı min/max aralıkları - DAHA GENİŞ ARALIK
const getRangesForWeek = (week) => {
  const data = FETAL_DATA[week] || FETAL_DATA[23];
  return {
    bpd: { min: Math.floor(data.bpd.p5 * 0.7), max: Math.ceil(data.bpd.p95 * 1.4) },
    hc: { min: Math.floor(data.hc.p5 * 0.7), max: Math.ceil(data.hc.p95 * 1.4) },
    fromen: { min: 10, max: 60 },
    goztepe: { min: 8, max: 45 },
    goz: { min: 4, max: 25 },
    burun: { min: 3, max: 25 },
    bioc_cap: { min: 20, max: 80 },
    agiz_capi: { min: 5, max: 30 },
    ust_dudak: { min: 2, max: 18 },
    cene: { min: 8, max: 50 },
    onarka_bas: { min: 30, max: 120 }  // Önarka baş (OFD) ölçümü
  };
};

// Ölçü alanları - slider config (Kullanıcı isteklerine göre Türkçe isimler)
const MEASUREMENT_LABELS = {
  fromen: { label: 'Fromen', unit: 'mm', color: '#ef4444', step: 0.1 },
  burun: { label: 'Burun', unit: 'mm', color: '#f97316', step: 0.1 },
  goztepe: { label: 'Göztepe', unit: 'mm', color: '#eab308', step: 0.1 },
  bioc_cap: { label: 'BiocÇap', unit: 'mm', color: '#22c55e', step: 0.1 },
  cene: { label: 'Çene', unit: 'mm', color: '#14b8a6', step: 0.1 },
  agiz_capi: { label: 'Ağızçapı', unit: 'mm', color: '#3b82f6', step: 0.1 },
  onarka_bas: { label: 'Önarka baş', unit: 'mm', color: '#6366f1', step: 0.1 },
  bpd: { label: 'BPD', unit: 'mm', color: '#8b5cf6', step: 0.1 },
  hc: { label: 'HC', unit: 'mm', color: '#d946ef', step: 0.5 },
  goz: { label: 'Göz', unit: 'mm', color: '#ec4899', step: 0.1 },
  ust_dudak: { label: 'Üst Dudak', unit: 'mm', color: '#a855f7', step: 0.1 },
};

const MEASUREMENT_KEYS = ['fromen', 'burun', 'goztepe', 'bioc_cap', 'cene', 'agiz_capi', 'onarka_bas', 'bpd', 'hc', 'goz', 'ust_dudak'];

export const LiveEditorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');

  // States
  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Week and measurements
  const [selectedWeek, setSelectedWeek] = useState(23);
  const [measurements, setMeasurements] = useState(() => getDefaultsForWeek(23));
  const [gender, setGender] = useState('female');

  // Get dynamic ranges based on selected week
  const ranges = useMemo(() => getRangesForWeek(selectedWeek), [selectedWeek]);

  // Fetch scan data
  const fetchScanData = useCallback(async () => {
    if (!scanId) {
      toast.error('Tarama ID bulunamadı');
      navigate('/doctor/patients');
      return;
    }

    try {
      setLoading(true);
      const scanData = await scansApi.get(scanId);
      setScan(scanData);

      const patientData = await patientsApi.get(scanData.patient_id);
      setPatient(patientData);

      // Set week from patient data
      const patientWeek = patientData.pregnancy_week || 23;
      setSelectedWeek(patientWeek);

      // Load saved measurements or use defaults for the week
      if (scanData.metadata?.measurements) {
        setMeasurements(prev => ({ ...getDefaultsForWeek(patientWeek), ...scanData.metadata.measurements }));
      } else {
        setMeasurements(getDefaultsForWeek(patientWeek));
      }
      
      if (scanData.metadata?.gender) {
        setGender(scanData.metadata.gender);
      }

    } catch (error) {
      toast.error('Veri yüklenirken hata oluştu');
      navigate('/doctor/patients');
    } finally {
      setLoading(false);
    }
  }, [scanId, navigate]);

  useEffect(() => {
    fetchScanData();
  }, [fetchScanData]);

  // Handle week change - update defaults and ranges
  const handleWeekChange = useCallback((week) => {
    const weekNum = parseInt(week);
    setSelectedWeek(weekNum);
    setMeasurements(getDefaultsForWeek(weekNum));
  }, []);

  // Measurement change handler - more permissive
  const handleMeasurementChange = useCallback((key, value) => {
    // String olarak gelen değeri direkt parse et
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (!isNaN(numValue)) {
      const range = ranges[key];
      // Range içinde tut
      const clampedValue = Math.max(range.min, Math.min(range.max, numValue));
      setMeasurements(prev => ({ ...prev, [key]: clampedValue }));
    }
  }, [ranges]);

  // Gender change handler
  const handleGenderChange = useCallback((newGender) => {
    setGender(newGender);
  }, []);

  // Reset to defaults for current week
  const handleReset = useCallback(() => {
    setMeasurements(getDefaultsForWeek(selectedWeek));
    toast.info(`${selectedWeek}. hafta varsayılan değerleri yüklendi`);
  }, [selectedWeek]);

  // Save measurements to scan
  const handleSave = async () => {
    setSaving(true);
    try {
      await scansApi.update(scanId, {
        metadata: { measurements, gender, week: selectedWeek },
        status: 'generated' // Durumu "tamamlandı" olarak işaretle
      });
      toast.success('Analiz kaydedildi!');
    } catch (error) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="3D Fetal Editor">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="3D Fetal Yüz Editörü" 
      subtitle={patient ? `${patient.first_name} ${patient.last_name}` : ''}
    >
      <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="live-editor-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/doctor/patients/${patient?.id}`)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Sıfırla
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#5A67D8] hover:bg-[#4F46E5]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0 overflow-hidden">
          {/* Left Panel - Controls with Tabs */}
          <div className="col-span-4 flex flex-col min-h-0 overflow-hidden">
            <Card className="flex-1 border-0 shadow-lg overflow-hidden flex flex-col min-h-0">
              <Tabs defaultValue="measurements" className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <TabsList className="grid w-full grid-cols-2 rounded-none border-b flex-shrink-0">
                  <TabsTrigger value="measurements" className="flex items-center gap-1.5 text-xs">
                    <Ruler className="w-3.5 h-3.5" />
                    Ölçüler
                  </TabsTrigger>
                  <TabsTrigger value="percentiles" className="flex items-center gap-1.5 text-xs">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Persentil
                  </TabsTrigger>
                </TabsList>

                {/* Measurements Tab */}
                <TabsContent value="measurements" className="flex-1 mt-0 data-[state=inactive]:hidden">
                  <div className="h-full overflow-y-auto p-3 space-y-2" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                  {/* Week & Gender in one row */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Hafta</Label>
                      <Select value={selectedWeek.toString()} onValueChange={handleWeekChange}>
                        <SelectTrigger className="w-full h-8 text-xs">
                          <SelectValue placeholder="Hafta" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 23 }, (_, i) => i + 18).map((week) => (
                            <SelectItem key={week} value={week.toString()}>
                              {week}. Hafta
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Cinsiyet</Label>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleGenderChange('female')}
                          className={`flex-1 py-1.5 px-2 rounded border text-xs font-medium transition-all ${
                            gender === 'female'
                              ? 'border-pink-400 bg-pink-50 text-pink-600'
                              : 'border-slate-200 hover:border-pink-200'
                          }`}
                        >
                          ♀ Kız
                        </button>
                        <button
                          onClick={() => handleGenderChange('male')}
                          className={`flex-1 py-1.5 px-2 rounded border text-xs font-medium transition-all ${
                            gender === 'male'
                              ? 'border-blue-400 bg-blue-50 text-blue-600'
                              : 'border-slate-200 hover:border-blue-200'
                          }`}
                        >
                          ♂ Erkek
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-slate-200 pt-2"></div>

                  {/* Measurement Sliders with Percentile Indicators */}
                  {MEASUREMENT_KEYS.map((key) => {
                    const config = MEASUREMENT_LABELS[key];
                    const range = ranges[key];
                    const value = measurements[key] || range.min;
                    const hasPercentile = ['bpd', 'hc', 'burun', 'onarka_bas', 'goztepe', 'bioc_cap', 'cene'].includes(key);
                    
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-600">{config.label}</Label>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              defaultValue={value}
                              key={`${key}-${selectedWeek}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  handleMeasurementChange(key, val);
                                }
                              }}
                              className="w-16 h-6 text-xs text-right px-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              step={config.step}
                              min={range.min}
                              max={range.max}
                            />
                            <span className="text-[10px] text-slate-400">{config.unit}</span>
                          </div>
                        </div>
                        <Slider
                          value={[value]}
                          min={range.min}
                          max={range.max}
                          step={config.step}
                          onValueChange={([val]) => handleMeasurementChange(key, val)}
                          className="cursor-pointer"
                          data-testid={`slider-${key}`}
                        />
                        {/* Percentile Range Indicator */}
                        {hasPercentile ? (
                          <PercentileRangeIndicator 
                            measurementType={key === 'onarka_bas' ? 'ofd' : key}
                            value={value}
                            week={selectedWeek}
                          />
                        ) : (
                          <div className="flex justify-between text-[9px] text-slate-400">
                            <span>{range.min}</span>
                            <span>{range.max}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </TabsContent>

                {/* Percentiles Tab */}
                <TabsContent value="percentiles" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0 bg-slate-900 data-[state=inactive]:hidden" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                  {/* Summary */}
                  <PercentileSummary measurements={measurements} week={selectedWeek} />

                  {/* Individual Charts */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Büyüme Eğrileri
                    </h4>
                    
                    <PercentileChart 
                      measurementType="bpd"
                      currentValue={measurements.bpd}
                      currentWeek={selectedWeek}
                      height={160}
                      showLegend={false}
                    />
                    
                    <PercentileChart 
                      measurementType="hc"
                      currentValue={measurements.hc}
                      currentWeek={selectedWeek}
                      height={160}
                      showLegend={false}
                    />

                    <PercentileChart 
                      measurementType="burun"
                      currentValue={measurements.burun}
                      currentWeek={selectedWeek}
                      height={160}
                      showLegend={false}
                    />

                    <PercentileChart 
                      measurementType="cene"
                      currentValue={measurements.cene}
                      currentWeek={selectedWeek}
                      height={160}
                      showLegend={true}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Right Panel - 3D Viewer */}
          <div className="col-span-8 flex flex-col min-h-0">
            <Card className="flex-1 border-0 shadow-lg overflow-hidden flex flex-col bg-[#0a1628]">
              <CardHeader className="py-3 px-4 border-b border-slate-700/50 flex-shrink-0 bg-[#0d1f35]">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2 text-cyan-300">
                    <Move3d className="w-4 h-4 text-cyan-400" />
                    3D Fetal Model
                    <span className="text-xs text-slate-500 ml-2">
                      ({selectedWeek}. hafta - 360° döndürmek için sürükleyin)
                    </span>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="text-slate-400 hover:text-cyan-300 h-7 w-7 p-0"
                    >
                      <RotateCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 p-0 overflow-hidden">
                <FetalHead3DViewer 
                  measurements={measurements} 
                  gender={gender}
                  week={selectedWeek}
                  className="w-full h-full"
                  onMeasurementChange={handleMeasurementChange}
                />
              </CardContent>
              
              {/* Bottom status bar */}
              <div className="px-4 py-2 border-t border-slate-700/50 flex-shrink-0 bg-[#0d1f35]/50">
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${gender === 'female' ? 'text-pink-400' : 'text-cyan-400'}`}>
                    {gender === 'female' ? '♀ Kız Bebek' : '♂ Erkek Bebek'}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-emerald-400 flex items-center gap-1">
                      ✨ Çift tıkla = FaceWarp
                    </span>
                    <span className="text-slate-500 flex items-center gap-1">
                      <Move3d className="w-3 h-3" />
                      Döndür: Sürükle
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Bottom Status Bar */}
        <div className="mt-4 flex-shrink-0">
          <Card className="border-0 shadow-sm bg-slate-50">
            <CardContent className="py-2 px-4">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Baby className="w-3 h-3" />
                    {patient?.first_name} {patient?.last_name}
                  </span>
                  <span>{selectedWeek}. hafta</span>
                </div>
                <div className="flex items-center gap-4">
                  <span>BPD: {measurements.bpd?.toFixed(1)}mm</span>
                  <span>HC: {measurements.hc?.toFixed(1)}mm</span>
                  <span className="text-slate-400">|</span>
                  <span>NeoBreed AI v3.0</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LiveEditorPage;
